import { alpha, createMuiTheme, darken, lighten, Theme } from '@material-ui/core';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import { TypographyOptions, TypographyStyleOptions } from '@material-ui/core/styles/createTypography';
import { Caveat, Lato } from './fonts/AppFonts';

const DARKEN_COEF = 0.1;
const LIGHTEN_COEF = 0.1;

// Augment Typography Props with new variants
declare module '@material-ui/core/Typography/Typography' {
    interface TypographyPropsVariantOverrides {
        h1Sub: true;
        h2Sub: true;
        h3Sub: true;
        h4Sub: true;
        h6Sub: true;
        body3: true;
        label: true;
    }
}

// Augment Typography Options with new variants
declare module '@material-ui/core/styles/createTypography' {
    interface TypographyOptions {
        h1Sub?: TypographyStyleOptions;
        h2Sub?: TypographyStyleOptions;
        h3Sub?: TypographyStyleOptions;
        h4Sub?: TypographyStyleOptions;
        h6Sub?: TypographyStyleOptions;
        body3?: TypographyStyleOptions;
        label?: TypographyStyleOptions;
    }
}

// Augment Button Props with new variants
declare module '@material-ui/core/Button/Button' {
    interface ButtonPropsVariantOverrides {
        inverted: true;
    }
}

/**
 * Creates a responsive typography variant
 */
const breakpoints = createBreakpoints({});

function responsiveTypography(fontSize: number, lineHeight: number): TypographyStyleOptions {
    return {
        fontSize: `${fontSize}rem`,
        lineHeight: `${lineHeight}rem`,
        [breakpoints.down('sm')]: {
            fontSize: `${fontSize * 0.88}rem`,
            lineHeight: `${lineHeight * 0.8}rem`,
        },
    };
}

const typographyOptions: TypographyOptions = {
    fontFamily: 'Lato, Roboto, Helvetica, Arial, sans-serif',
    fontSize: 16,
    h1: {
        fontWeight: 400,
        textTransform: 'uppercase',
        ...responsiveTypography(2.9, 3.5),
    },
    h1Sub: {
        fontWeight: 700,
        ...responsiveTypography(1.5, 2),
    },
    body1: {
        fontWeight: 400,
        ...responsiveTypography(1.1, 1.5),
    },
    label: {
        fontWeight: 700,
        ...responsiveTypography(1.1, 1.3),
    },
    h2: {
        fontWeight: 400,
        ...responsiveTypography(2.9, 3.5),
    },
    h2Sub: {
        fontWeight: 700,
        ...responsiveTypography(1.5, 2),
    },
    body2: {
        fontWeight: 400,
        ...responsiveTypography(1.1, 1.5),
    },
    h3: {
        fontWeight: 400,
        ...responsiveTypography(2.3, 2.8),
    },
    h3Sub: {
        fontWeight: 700,
        ...responsiveTypography(1.3, 1.5),
    },
    body3: {
        fontWeight: 400,
        ...responsiveTypography(1.1, 1.5),
    },
    h4: {
        fontWeight: 700,
        textTransform: 'uppercase',
        ...responsiveTypography(1.8, 2.8),
    },
    h4Sub: {
        fontWeight: 700,
        ...responsiveTypography(1, 1),
    },
    h6: {
        fontWeight: 700,
        ...responsiveTypography(1.5, 2),
    },
    h6Sub: {
        fontWeight: 400,
        fontStyle: 'italic',
        ...responsiveTypography(1, 1.5),
    },
    button: {
        fontWeight: 700,
        textTransform: 'none',
        ...responsiveTypography(1.4, 1.7),
    },
};

const theme = createMuiTheme({
    typography: typographyOptions,
    breakpoints: {
        values: {
            xs: 0,
            sm: 800,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    palette: {
        success: {
            light: lighten('#07A63E', LIGHTEN_COEF),
            main: '#07A63E',
            dark: darken('#07A63E', DARKEN_COEF),
            contrastText: '#fff',
        },
        error: {
            light: lighten('#A60C07', LIGHTEN_COEF),
            main: '#A60C07',
            dark: darken('#A60C07', DARKEN_COEF),
            contrastText: '#fff',
        },
        info: {
            light: lighten('#075FA6', LIGHTEN_COEF),
            main: '#075FA6',
            dark: darken('#075FA6', DARKEN_COEF),
            contrastText: '#fff',
        },
        warning: {
            light: lighten('#D94C09', LIGHTEN_COEF),
            main: '#D94C09',
            dark: darken('#D94C09', DARKEN_COEF),
            contrastText: '#fff',
        },
        primary: {
            light: lighten('#a51b17', LIGHTEN_COEF),
            main: '#a51b17',
            dark: darken('#a51b17', DARKEN_COEF),
            contrastText: '#fff',
        },
        secondary: {
            light: lighten('#3C3C3C', LIGHTEN_COEF),
            main: '#3C3C3C',
            dark: darken('#3C3C3C', DARKEN_COEF),
            contrastText: '#fff',
        },
        text: {
            primary: '#5C5C5C',
        },
        grey: {
            '50': '#fafafa',
            '100': '#f5f5f5',
            '200': '#eeeeee',
            '300': '#e8e8e8',
            '400': '#bdbdbd',
            '500': '#9e9e9e',
            '600': '#757575',
            '700': '#5c5c5c',
            '800': '#333333',
            '900': '#2e2e2e',
            A100: '#d5d5d5',
            A200: '#aaaaaa',
            A400: '#303030',
            A700: '#616161',
        },
        background: {
            default: '#fff',
            paper: '#fff',
        },
    },
    shape: {
        borderRadius: 0,
    },
});

theme.components = {
    MuiCssBaseline: {
        styleOverrides: {
            '@global': {
                '@font-face': [...Lato, ...Caveat],
            },
        },
    },
    MuiTypography: {
        variants: [
            {
                props: {
                    variant: 'h6Sub',
                },
                style: {
                    display: 'block',
                },
            },
        ],
        styleOverrides: {
            gutterBottom: {
                marginBottom: theme.spacing(3),
                display: 'block',
            },
        },
    },
    MuiButton: {
        variants: [
            {
                props: {
                    variant: 'inverted',
                },
                style: {
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.contrastText,
                    '&:hover': {
                        backgroundColor: darken(theme.palette.primary.contrastText, DARKEN_COEF),
                    },
                },
            },
            {
                props: {
                    color: 'primary',
                    variant: 'inverted',
                },
                style: {
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.contrastText,
                    '&:hover': {
                        backgroundColor: darken(theme.palette.primary.contrastText, DARKEN_COEF),
                    },
                },
            },
            {
                props: {
                    color: 'secondary',
                    variant: 'inverted',
                },
                style: {
                    color: theme.palette.secondary.main,
                    backgroundColor: theme.palette.secondary.contrastText,
                    '&:hover': {
                        backgroundColor: darken(theme.palette.secondary.contrastText, DARKEN_COEF),
                    },
                },
            },
            {
                props: {
                    size: 'small',
                    variant: 'contained',
                },
                style: {
                    fontWeight: 400,
                    fontSize: '0.8rem',
                },
            },
        ],
        styleOverrides: {
            root: {
                padding: `${theme.spacing(1.2)} ${theme.spacing(4.4)}`,
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            sizeSmall: {
                padding: theme.spacing(0.25),
            },
        },
    },
    MuiDialog: {
        defaultProps: {
            maxWidth: 'md',
            fullWidth: true,
        },
        styleOverrides: {
            paper: {
                margin: theme.spacing(2),
            },
            paperFullWidth: {
                width: `calc(100% - ${theme.spacing(4)})`,
            },
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: {
                justifyContent: 'center',
            },
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                '& :not([class*="MuiGrid-container"]) [class*="MuiGrid-container"]': {
                    marginTop: 0,
                    marginBottom: 0,
                },
            },
        },
    },
    MuiGrid: {
        styleOverrides: {
            'direction-xs-column': {
                '&>[class*="MuiGrid-item"]': {
                    width: '100%',
                },
            },
            'direction-xs-column-reverse': {
                '&>[class*="MuiGrid-item"]': {
                    width: '100%',
                },
            },
        },
    },
    MuiAppBar: {
        styleOverrides: {
            colorTransparent: {
                backgroundColor: alpha(theme.palette.common.white, 0.75),
                color: theme.palette.grey[700],
                border: 'none',
                '& [class*="MuiButton-root"]': {
                    color: 'inherit',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        color: theme.palette.primary.main,
                    },
                },
            },
        },
    },
    MuiMenu: {
        defaultProps: {
            disableScrollLock: true,
        },
    },
    MuiListItem: {
        styleOverrides: {
            selected: {
                '& [class*="MuiListItemText-primary"]': {
                    fontWeight: 700,
                },
            },
        },
    },
    MuiDivider: {
        styleOverrides: {
            light: {
                borderColor: theme.palette.common.white,
            },
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                backgroundColor: theme.palette.common.white,
                boxShadow: theme.shadows[3],
                color: theme.palette.text.secondary,
                padding: theme.spacing(1.2),
            },
            arrow: {
                color: theme.palette.common.white,
            },
        },
    },
};

const acmeAppTheme = createMuiTheme(theme, {
    palette: {
        primary: {
            main: '#0b911d',
        },
        secondary: {
            main: '#115a2f',
        },
    },
});

export type AppThemeId = 'default' | 'acme';

const APP_THEMES: Record<AppThemeId, Theme> = {
    default: theme,
    acme: acmeAppTheme,
};

export const getAppThemeById = (themeId?: string): Theme => {
    if (!themeId) {
        return APP_THEMES.default;
    }

    return APP_THEMES[themeId as AppThemeId] || APP_THEMES.default;
};

export default APP_THEMES.default;
